import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {DOCUMENTO_MAX, type TipoDocumento} from '@/lib/documento'
import {validateCedula, validateEmail, validatePhone, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

export type PersonaIdentity = {
  name: string
  last_name: string
  cedula: string
  tipo_documento: TipoDocumento
  email: string
  phone: string
}

type IdentityFieldErrors = Partial<Record<keyof PersonaIdentity, string>>

export function identityPayload(identity: PersonaIdentity) {
  return {
    name: identity.name.trim(),
    last_name: identity.last_name.trim(),
    cedula: identity.cedula.trim(),
    tipo_documento: identity.tipo_documento,
    email: identity.email.trim(),
    phone: identity.phone.trim() || undefined,
  }
}

/** Email is always required here: it is the invite target. */
export const IDENTITY_VALIDATORS: Record<
  keyof PersonaIdentity,
  (raw: string, identity: PersonaIdentity) => string | null
> = {
  name: validateRequired,
  last_name: validateRequired,
  // El formato depende del tipo de documento: por eso el validador recibe la identidad entera.
  cedula: (raw, identity) => validateCedula(raw, identity.tipo_documento),
  tipo_documento: () => null,
  email: validateEmail,
  phone: (raw) => (raw.trim() ? validatePhone(raw) : null),
}

function validateKeys(identity: PersonaIdentity, keys: readonly (keyof PersonaIdentity)[]): IdentityFieldErrors {
  const errors: IdentityFieldErrors = {}
  for (const key of keys) {
    const problem = IDENTITY_VALIDATORS[key](identity[key], identity)
    if (problem) errors[key] = problem
  }
  return errors
}

export function validateIdentity(identity: PersonaIdentity): IdentityFieldErrors {
  return validateKeys(identity, Object.keys(IDENTITY_VALIDATORS) as (keyof PersonaIdentity)[])
}

/** La persona de batería no tiene login: su paquete va en papel, así que no se le exige contacto. */
export function validateBasicIdentity(identity: PersonaIdentity): IdentityFieldErrors {
  return validateKeys(identity, ['name', 'last_name', 'cedula'])
}

export function clearChangedIdentityErrors(
  prev: IdentityFieldErrors,
  next: PersonaIdentity,
  current: PersonaIdentity
): IdentityFieldErrors {
  const cleared = {...prev}
  for (const key of Object.keys(next) as (keyof PersonaIdentity)[]) {
    if (next[key] !== current[key]) delete cleared[key]
  }
  return cleared
}

type PersonaFieldsProps = {
  value: PersonaIdentity
  onChange: (next: PersonaIdentity) => void
  tKey: string
  testPrefix: string
  errors?: Partial<Record<keyof PersonaIdentity, string>>
  onBlurField?: (field: keyof PersonaIdentity) => void
}

export function PersonaBasicFields({value, onChange, tKey, testPrefix, errors, onBlurField}: PersonaFieldsProps) {
  const {t} = useTranslation()
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-name`}>{t(`${tKey}.name`)}</Label>
        <Input
          maxLength={tope('NOM')}
          id={`${testPrefix}-name`}
          data-testid={`${testPrefix}-name`}
          value={value.name}
          onChange={(e) => onChange({...value, name: e.target.value})}
          onBlur={() => onBlurField?.('name')}
          aria-invalid={!!errors?.name}
          required
        />
        <FieldError error={errors?.name} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-last-name`}>{t(`${tKey}.lastName`)}</Label>
        <Input
          maxLength={tope('NOM')}
          id={`${testPrefix}-last-name`}
          data-testid={`${testPrefix}-last-name`}
          value={value.last_name}
          onChange={(e) => onChange({...value, last_name: e.target.value})}
          onBlur={() => onBlurField?.('last_name')}
          aria-invalid={!!errors?.last_name}
          required
        />
        <FieldError error={errors?.last_name} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-cedula`}>{t('documento.label')}</Label>
        <div className="flex gap-2">
          <TipoDocumentoSelect
            value={value.tipo_documento}
            onChange={(tipo) => onChange({...value, tipo_documento: tipo})}
            testId={`${testPrefix}-tipo-documento`}
          />
          <Input
            id={`${testPrefix}-cedula`}
            data-testid={`${testPrefix}-cedula`}
            className="flex-1 font-mono"
            maxLength={DOCUMENTO_MAX[value.tipo_documento]}
            value={value.cedula}
            onChange={(e) => onChange({...value, cedula: e.target.value})}
            onBlur={() => onBlurField?.('cedula')}
            aria-invalid={!!errors?.cedula}
            required
          />
        </div>
        <FieldError error={errors?.cedula} />
      </div>
    </>
  )
}

export function PersonaContactFields({value, onChange, tKey, testPrefix, errors, onBlurField}: PersonaFieldsProps) {
  const {t} = useTranslation()
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-email`}>{t(`${tKey}.email`)}</Label>
        <Input
          maxLength={tope('MAIL')}
          id={`${testPrefix}-email`}
          data-testid={`${testPrefix}-email`}
          type="email"
          value={value.email}
          onChange={(e) => onChange({...value, email: e.target.value})}
          onBlur={() => onBlurField?.('email')}
          aria-invalid={!!errors?.email}
          required
        />
        <FieldError error={errors?.email} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-phone`}>{t(`${tKey}.phone`)}</Label>
        <Input
          maxLength={tope('TEL')}
          id={`${testPrefix}-phone`}
          data-testid={`${testPrefix}-phone`}
          className="font-mono"
          value={value.phone}
          onChange={(e) => onChange({...value, phone: e.target.value})}
          onBlur={() => onBlurField?.('phone')}
          aria-invalid={!!errors?.phone}
        />
        <FieldError error={errors?.phone} />
      </div>
    </>
  )
}

export function PersonaIdentityFields(props: PersonaFieldsProps) {
  return (
    <>
      <PersonaBasicFields {...props} />
      <PersonaContactFields {...props} />
    </>
  )
}
